import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Grid, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, CircularProgress, FormControl, InputLabel, MenuItem, Select,
    SelectChangeEvent, Typography, Chip, InputAdornment
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getActiveProjects } from "../../api/projectApi";
import { getUserProject } from "../../api/userApi";
import { profiles } from "../../contexts/constants/profiles";
import { getSubcontractCalendar } from "../../api/subcontractCalendarApi";
import { Project } from "../../types/projectTypes";
import { ResourceType } from "../../types/subcontractTypes";
import {
    SubcontractCalendarEntry,
    SubcontractCalendarRow,
    resourceTypeLabel
} from "../../types/subcontractCalendarTypes";
import { formatRUT } from "../../utils/validation";
import logo from "../../assets/images/logoElecnor.png";

/** Valor que se muestra en los días sin actividad reportada. */
const NO_ACTIVITY = "-";

/** Estilo de las celdas de cabecera, con el azul acero del mockup y texto blanco. */
const HEADER_CELL = {
    fontSize: "0.78rem",
    p: "6px 8px",
    fontWeight: "bold",
    backgroundColor: "#5b7fa6",
    color: "#ffffff",
    borderRight: "1px solid rgba(255,255,255,0.25)",
    whiteSpace: "nowrap"
} as const;

/** Estilo de las tres columnas fijas del cuerpo, que acompañan el scroll horizontal. */
const BODY_STICKY_CELL = {
    fontSize: "0.78rem",
    p: "6px 8px",
    position: "sticky",
    backgroundColor: "white",
    borderRight: "1px solid #eee",
    zIndex: 2,
    whiteSpace: "nowrap"
} as const;

const SubcontractCalendar: React.FC = () => {
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<number>();
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [filterTrigger, setFilterTrigger] = useState(false);
    const [loading, setLoading] = useState(false);

    const [entries, setEntries] = useState<SubcontractCalendarEntry[]>([]);

    // Botonera de la seccion 5.2: con ambos activos se obtiene el Modo Mixto, donde la
    // empresa aparece una sola vez por tipo de recurso y la distincion queda en la columna Tipo.
    const [visibleTypes, setVisibleTypes] = useState<ResourceType[]>(['PERSONNEL', 'EQUIPMENT']);
    const [search, setSearch] = useState("");

    useEffect(() => {
        fetchProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchCalendarData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterTrigger]);

    const fetchProjects = async () => {
        try {
            setLoading(true);
            const userStr = localStorage.getItem("user");
            let role = "";
            let rut = "";
            if (userStr) {
                const user = JSON.parse(userStr);
                role = user.rols?.description;
                rut = user.identificationNum;
            }

            let projectsData: Project[] = [];
            // Misma logica de visibilidad que el resto de los calendarios: Director, Gerente
            // y Visualizador ven todos los proyectos activos; el resto solo los asignados.
            if (role === profiles[0].name || role === profiles[2].name || role === profiles[3].name) {
                projectsData = await getActiveProjects();
            } else {
                const userProjects = await getUserProject(rut);
                projectsData = Array.isArray(userProjects) ? userProjects : [];
            }

            setProjects(projectsData.filter((p) => p.status === undefined || p.status === true));
        } catch (error) {
            console.error("Error al obtener proyectos:", error);
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString: string): string => (dateString ? `${dateString}T00:00:00` : "");

    const formatDateDisplay = (dateStr: string): string => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${day}/${month}/${year}`;
    };

    const fetchCalendarData = async () => {
        if (!selectedProject) return;
        try {
            setLoading(true);
            const data = await getSubcontractCalendar(
                formatDateTime(startDate),
                formatDateTime(endDate),
                selectedProject
            );
            setEntries(data);
        } catch (error) {
            console.error("Error al obtener el calendario de subcontratos:", error);
            setEntries([]);
        } finally {
            setLoading(false);
        }
    };

    const handleProjectChange = (event: SelectChangeEvent<number>) => {
        setSelectedProject(Number(event.target.value));
    };

    /** Activa o desactiva un tipo de recurso en la botonera. */
    const toggleResourceType = (resourceType: ResourceType) => {
        setVisibleTypes(prev =>
            prev.includes(resourceType)
                ? prev.filter(item => item !== resourceType)
                : [...prev, resourceType]
        );
    };

    // Datos ya filtrados por botonera y buscador, pivoteados a matriz.
    const { dates, rows, cells } = useMemo(() => {
        const term = search.trim().toLowerCase();

        const filtered = entries.filter(entry => {
            if (!visibleTypes.includes(entry.resourceType)) return false;
            if (!term) return true;
            return entry.businessName.toLowerCase().includes(term)
                || entry.rut.toLowerCase().includes(term)
                || formatRUT(entry.rut).toLowerCase().includes(term);
        });

        const uniqueDates = Array.from(new Set(filtered.map(e => e.activityDate.split("T")[0]))).sort();

        // Una fila por empresa y tipo de recurso: si una empresa aporto personal y equipos,
        // se muestra en dos filas continuas, nunca combinadas.
        const rowMap = new Map<string, SubcontractCalendarRow>();
        filtered.forEach(entry => {
            const key = `${entry.rut}|${entry.resourceType}`;
            if (!rowMap.has(key)) {
                rowMap.set(key, {
                    key,
                    rut: entry.rut,
                    businessName: entry.businessName,
                    resourceType: entry.resourceType,
                    showIdentity: true
                });
            }
        });

        // Personal siempre antes que Equipos dentro de una misma empresa. No sirve ordenar
        // alfabeticamente por el enum, porque EQUIPMENT precede a PERSONNEL.
        const typeOrder: Record<ResourceType, number> = { PERSONNEL: 0, EQUIPMENT: 1 };

        const orderedRows = Array.from(rowMap.values()).sort((a, b) => {
            const byName = a.businessName.localeCompare(b.businessName);
            if (byName !== 0) return byName;
            return typeOrder[a.resourceType] - typeOrder[b.resourceType];
        });

        // El nombre y el RUT se muestran solo en la primera fila de cada empresa.
        let previousRut: string | null = null;
        orderedRows.forEach(row => {
            row.showIdentity = row.rut !== previousRut;
            previousRut = row.rut;
        });

        const cellMap: Record<string, Record<string, string>> = {};
        orderedRows.forEach(row => {
            cellMap[row.key] = {};
            uniqueDates.forEach(date => {
                const entry = filtered.find(e =>
                    e.activityDate.startsWith(date) &&
                    e.rut === row.rut &&
                    e.resourceType === row.resourceType
                );
                cellMap[row.key][date] = entry ? entry.cip : NO_ACTIVITY;
            });
        });

        return { dates: uniqueDates, rows: orderedRows, cells: cellMap };
    }, [entries, visibleTypes, search]);

    /**
     * Exporta el calendario a un único archivo con dos pestañas, según la sección 5.3.
     * Respeta los filtros activos de botonera y buscador.
     */
    const exportToExcel = async () => {
        const workbook = new ExcelJS.Workbook();

        // --- Pestaña 1: Vista Calendario (matriz mensual) ---
        const gridSheet = workbook.addWorksheet("Vista Calendario");

        const imageResponse = await fetch(logo);
        const imageBuffer = await imageResponse.arrayBuffer();
        const imageId = workbook.addImage({ buffer: imageBuffer, extension: "png" });
        gridSheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 160, height: 60 } });

        gridSheet.addRow([]);
        gridSheet.addRow([]);
        gridSheet.addRow([]);
        gridSheet.addRow([]);

        const gridHeader = ["RUT", "Razón Social", "Tipo de Recurso", ...dates.map(formatDateDisplay)];
        const gridHeaderRow = gridSheet.addRow(gridHeader);
        gridHeaderRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "0070C0" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
        });

        // Sin celdas combinadas: el RUT y la razon social se repiten en la fila de Personal
        // y en la de Equipos, para que los filtros de Excel mantengan ambas visibles.
        rows.forEach((row, rowIndex) => {
            const rowData = [
                formatRUT(row.rut),
                row.businessName,
                resourceTypeLabel(row.resourceType),
                ...dates.map(date => cells[row.key]?.[date] || NO_ACTIVITY)
            ];
            const excelRow = gridSheet.addRow(rowData);
            excelRow.eachCell((cell, colIndex) => {
                cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
                if (rowIndex % 2 === 0) {
                    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "DCE6F1" } };
                }
                if (colIndex > 3) {
                    cell.alignment = { horizontal: "center" };
                }
            });
        });

        gridSheet.getColumn(1).width = 15;
        gridSheet.getColumn(2).width = 35;
        gridSheet.getColumn(3).width = 18;
        for (let i = 4; i <= gridHeader.length; i++) {
            gridSheet.getColumn(i).width = 14;
        }
        // Autofiltro sobre la cabecera para que las columnas se puedan filtrar directamente.
        gridSheet.autoFilter = {
            from: { row: gridHeaderRow.number, column: 1 },
            to: { row: gridHeaderRow.number, column: gridHeader.length }
        };

        // --- Pestaña 2: Base de Datos de Asignaciones (lista plana) ---
        const listSheet = workbook.addWorksheet("Base de Datos de Asignaciones");

        const listHeader = ["Fecha", "RUT Subcontratista", "Razón Social", "Tipo de Recurso", "Actividad Asignada"];
        const listHeaderRow = listSheet.addRow(listHeader);
        listHeaderRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "0070C0" } };
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
        });

        // Solo se generan filas para los dias con actividad reportada.
        rows.forEach(row => {
            dates.forEach(date => {
                const value = cells[row.key]?.[date];
                if (!value || value === NO_ACTIVITY) return;
                const excelRow = listSheet.addRow([
                    formatDateDisplay(date),
                    formatRUT(row.rut),
                    row.businessName,
                    resourceTypeLabel(row.resourceType),
                    value
                ]);
                excelRow.eachCell((cell) => {
                    cell.border = { top: { style: "thin" }, bottom: { style: "thin" }, left: { style: "thin" }, right: { style: "thin" } };
                });
            });
        });

        listSheet.getColumn(1).width = 14;
        listSheet.getColumn(2).width = 18;
        listSheet.getColumn(3).width = 35;
        listSheet.getColumn(4).width = 18;
        listSheet.getColumn(5).width = 20;
        listSheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: listHeader.length } };

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, "Calendario_Subcontratos.xlsx");
    };

    return (
        <Box sx={{ p: 3 }}>
            {loading && <CircularProgress />}

            <Typography variant="h5" sx={{ fontWeight: "bold", color: "#004F86", mb: 2 }}>
                Calendario de Subcontratos
            </Typography>

            <Grid container spacing={2} sx={{ marginBottom: 1 }}>
                <Grid item xs={4}>
                    <FormControl fullWidth sx={{ marginBottom: 3 }}>
                        <InputLabel>Seleccionar Proyecto</InputLabel>
                        <Select
                            label="Seleccionar Proyecto"
                            value={selectedProject || ""}
                            onChange={handleProjectChange}
                        >
                            {projects.map((project) => (
                                <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        label="Fecha de Inicio"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        label="Fecha de Término"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
                <Button variant="contained" color="primary" onClick={() => setFilterTrigger(prev => !prev)}>
                    Filtrar
                </Button>
            </Box>

            {/* Buscador rápido y botonera de densidad de datos */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <TextField
                    placeholder="Búsqueda rápida por Nombre o RUT..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    sx={{ minWidth: 360, backgroundColor: "white" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: "#9e9e9e" }} />
                            </InputAdornment>
                        )
                    }}
                />

                <Button
                    onClick={() => toggleResourceType('PERSONNEL')}
                    startIcon={<PeopleAltIcon fontSize="small" />}
                    sx={{
                        textTransform: "none",
                        borderRadius: 5,
                        px: 2,
                        border: "1px solid",
                        borderColor: visibleTypes.includes('PERSONNEL') ? "#1565c0" : "#d0d0d0",
                        backgroundColor: visibleTypes.includes('PERSONNEL') ? "#e3f2fd" : "white",
                        color: visibleTypes.includes('PERSONNEL') ? "#1565c0" : "#616161",
                        "&:hover": { backgroundColor: visibleTypes.includes('PERSONNEL') ? "#d0e7ff" : "#f5f5f5" }
                    }}
                >
                    Mostrar Personal
                </Button>

                <Button
                    onClick={() => toggleResourceType('EQUIPMENT')}
                    startIcon={<LocalShippingIcon fontSize="small" />}
                    sx={{
                        textTransform: "none",
                        borderRadius: 5,
                        px: 2,
                        border: "1px solid",
                        borderColor: visibleTypes.includes('EQUIPMENT') ? "#e0a800" : "#d0d0d0",
                        backgroundColor: visibleTypes.includes('EQUIPMENT') ? "#fff8e1" : "white",
                        color: visibleTypes.includes('EQUIPMENT') ? "#8a6d00" : "#616161",
                        "&:hover": { backgroundColor: visibleTypes.includes('EQUIPMENT') ? "#ffecb3" : "#f5f5f5" }
                    }}
                >
                    Mostrar Equipos
                </Button>

                <Box sx={{ flexGrow: 1 }} />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={exportToExcel}
                    disabled={rows.length === 0}
                >
                    Exportar Excel
                </Button>
            </Box>

            {visibleTypes.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Active al menos uno de los botones para ver información.
                </Typography>
            )}

            <TableContainer
                component={Paper}
                sx={{ mt: 1, maxWidth: "100%", overflow: "auto", maxHeight: "calc(100vh - 380px)", boxShadow: 3 }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        {/* Orden de columnas segun el documento: Nombre, RUT, Tipo y luego los dias. */}
                        <TableRow sx={{ height: "36px" }}>
                            <TableCell sx={{ ...HEADER_CELL, position: "sticky", left: 0, top: 0, zIndex: 5, minWidth: "230px", maxWidth: "230px" }}>
                                Nombre
                            </TableCell>
                            <TableCell align="center" sx={{ ...HEADER_CELL, position: "sticky", left: "230px", top: 0, zIndex: 5, minWidth: "130px", maxWidth: "130px" }}>
                                RUT
                            </TableCell>
                            <TableCell align="center" sx={{ ...HEADER_CELL, position: "sticky", left: "360px", top: 0, zIndex: 5, minWidth: "110px", maxWidth: "110px" }}>
                                Tipo
                            </TableCell>
                            {dates.map(date => (
                                <TableCell key={date} align="center" sx={{ ...HEADER_CELL, fontSize: "0.72rem" }}>
                                    {formatDateDisplay(date)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3 + dates.length}>
                                    <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
                                        {selectedProject
                                            ? "No hay asignaciones de subcontratos para los filtros seleccionados."
                                            : "Seleccione un proyecto y presione Filtrar."}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : rows.map(row => {
                            const isPersonnel = row.resourceType === 'PERSONNEL';
                            return (
                                <TableRow key={row.key} hover>
                                    {/* Nombre y RUT solo en la primera fila de cada empresa. */}
                                    <TableCell sx={{ ...BODY_STICKY_CELL, left: 0, fontWeight: row.showIdentity ? 600 : 400 }}>
                                        {row.showIdentity ? row.businessName : ""}
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...BODY_STICKY_CELL, left: "230px" }}>
                                        {row.showIdentity ? formatRUT(row.rut) : ""}
                                    </TableCell>
                                    <TableCell align="center" sx={{ ...BODY_STICKY_CELL, left: "360px" }}>
                                        <Chip
                                            size="small"
                                            icon={isPersonnel
                                                ? <PeopleAltIcon sx={{ fontSize: 14 }} />
                                                : <LocalShippingIcon sx={{ fontSize: 14 }} />}
                                            label={resourceTypeLabel(row.resourceType)}
                                            sx={{
                                                height: 22,
                                                fontSize: "0.7rem",
                                                fontWeight: 600,
                                                backgroundColor: isPersonnel ? "#e3f2fd" : "#fff8e1",
                                                color: isPersonnel ? "#1565c0" : "#8a6d00",
                                                "& .MuiChip-icon": { color: "inherit" }
                                            }}
                                        />
                                    </TableCell>
                                    {dates.map(date => (
                                        <TableCell key={date} align="center" sx={{ fontSize: "0.75rem", p: "4px", whiteSpace: "nowrap" }}>
                                            {cells[row.key]?.[date] || NO_ACTIVITY}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default SubcontractCalendar;
